import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Component, ViewEncapsulation, ChangeDetectionStrategy, Input, ContentChild, InjectionToken, Injectable, Optional, Inject, NgModule } from '@angular/core';
import { DotPathWalkPipe, DotComponent, DotTypeOfPipe } from '@ng-dot/core';
import * as i5 from '@ng-dot/ui/frame';
import { DotFrameModule } from '@ng-dot/ui/frame';
import * as i3 from '@ng-dot/ui/loader';
import { DotLoaderModule } from '@ng-dot/ui/loader';
import * as i4 from '@ng-dot/ui/page-links';
import { DotPageLinksModule } from '@ng-dot/ui/page-links';
import * as i1 from '@ng-dot/text';

/**
 * Display Asynchronous Data with Resolve
 *
 * - Loading State
 * - Error State
 * - Empty State
 * - Page Links with Paginated Content
 *
 * @example Display Response
 * ```angular2html
 * <dot-render [resolve]="resolve">
 *     <ng-template #content let-response>
 *         {{response | json}}
 *     </ng-template>
 * </dot-render>
 * ```
 * @example Display Paginated Items
 * ```angular2html
 * <dot-render [resolve]="resolve">
 *     <ng-template #content let-items="items">
 *         {{items | json}}
 *     </ng-template>
 * </dot-render>
 * ```
 */
const _c0 = ["content"];
const _c1 = ["loader"];
const _c2 = ["empty"];
const _c3 = ["error"];
const _c4 = () => [];
const _c5 = (a0, a1) => ({
  $implicit: a0,
  items: a1
});
const _c6 = a0 => ({
  defineId: a0
});
const _c7 = a0 => ({
  $implicit: a0
});
function DotRenderComponent_Conditional_0_Conditional_0_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function DotRenderComponent_Conditional_0_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_0_Conditional_0_ng_container_0_Template, 1, 0, "ng-container", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.errorRef);
  }
}
function DotRenderComponent_Conditional_0_Conditional_1_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "dot-component", 1);
  }
  if (rf & 2) {
    i0.ɵɵproperty("option", ctx);
  }
}
function DotRenderComponent_Conditional_0_Conditional_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "dot-area");
    i0.ɵɵtext(1, " Rendering has something trouble. ");
    i0.ɵɵelementEnd();
  }
}
function DotRenderComponent_Conditional_0_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_0_Conditional_1_Conditional_0_Template, 1, 1, "dot-component", 1)(1, DotRenderComponent_Conditional_0_Conditional_1_Conditional_1_Template, 2, 0, "dot-area");
  }
  if (rf & 2) {
    let tmp_2_0;
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵconditional((tmp_2_0 = ctx_r0.configure.errorComponent) ? 0 : 1, tmp_2_0);
  }
}
function DotRenderComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_0_Conditional_0_Template, 1, 1, "ng-container")(1, DotRenderComponent_Conditional_0_Conditional_1_Template, 2, 1);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵconditional(ctx_r0.errorRef ? 0 : 1);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_0_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_0_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "dot-page-links", 4);
    i0.ɵɵlistener("changed$", function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_0_Conditional_1_Template_dot_page_links_changed__0_listener($event) {
      i0.ɵɵrestoreView(_r2);
      const ctx_r0 = i0.ɵɵnextContext(6);
      return i0.ɵɵresetView(ctx_r0.setPage($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const response_r3 = i0.ɵɵnextContext(4);
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("meta", response_r3.meta)("type", ctx_r0.pageLinkType)("linkLimit", ctx_r0.pageLinkLimit)("extend", ctx_r0.pageLinkExtend);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_0_ng_container_0_Template, 1, 0, "ng-container", 2)(1, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_0_Conditional_1_Template, 1, 4, "dot-page-links", 3);
  }
  if (rf & 2) {
    const items_r4 = i0.ɵɵnextContext();
    const response_r3 = i0.ɵɵnextContext(2);
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.contentRef)("ngTemplateOutletContext", i0.ɵɵpureFunction2(3, _c5, response_r3, items_r4));
    i0.ɵɵadvance();
    i0.ɵɵconditional(response_r3.meta.total > 0 ? 1 : -1);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "dot-component", 5);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(6);
    i0.ɵɵproperty("option", ctx)("data", i0.ɵɵpureFunction1(2, _c6, ctx_r0.defineId));
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Conditional_1_Conditional_0_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Conditional_1_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Conditional_1_Conditional_0_ng_container_0_Template, 1, 0, "ng-container", 2);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(7);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.emptyRef)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c7, ctx_r0.defineId));
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Conditional_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "dot-area");
    i0.ɵɵtext(1, " Not Found. ");
    i0.ɵɵelementEnd();
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Conditional_1_Conditional_0_Template, 1, 4, "ng-container")(1, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Conditional_1_Conditional_1_Template, 2, 0, "dot-area");
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(6);
    i0.ɵɵconditional(ctx_r0.emptyRef ? 0 : 1);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Conditional_0_Template, 1, 4, "dot-component", 5)(1, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Conditional_1_Template, 2, 1);
  }
  if (rf & 2) {
    let tmp_7_0;
    const ctx_r0 = i0.ɵɵnextContext(5);
    i0.ɵɵconditional((tmp_7_0 = ctx_r0.configure.emptyComponent) ? 0 : 1, tmp_7_0);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_0_Template, 2, 6);
    i0.ɵɵpipe(1, "pathWalk");
    i0.ɵɵtemplate(2, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Conditional_2_Template, 2, 1);
  }
  if (rf & 2) {
    const response_r3 = i0.ɵɵnextContext(2);
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵconditional(i0.ɵɵpipeBind2(1, 1, response_r3, ctx_r0.paginateFilterProperty) || ctx.length ? 0 : 2);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Conditional_0_Template, 3, 4);
    i0.ɵɵpipe(1, "pathWalk");
  }
  if (rf & 2) {
    let tmp_4_0;
    const response_r3 = i0.ɵɵnextContext();
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵconditional((tmp_4_0 = i0.ɵɵpipeBind3(1, 1, response_r3, ctx_r0.paginateItemsProperty, i0.ɵɵpureFunction0(5, _c4))) ? 0 : -1, tmp_4_0);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_0_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_0_ng_container_0_Template, 1, 0, "ng-container", 2);
  }
  if (rf & 2) {
    const response_r3 = i0.ɵɵnextContext(2);
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.contentRef)("ngTemplateOutletContext", i0.ɵɵpureFunction2(2, _c5, response_r3, response_r3));
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "dot-component", 5);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(5);
    i0.ɵɵproperty("option", ctx)("data", i0.ɵɵpureFunction1(2, _c6, ctx_r0.defineId));
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Conditional_1_Conditional_0_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Conditional_1_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Conditional_1_Conditional_0_ng_container_0_Template, 1, 0, "ng-container", 2);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(6);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.emptyRef)("ngTemplateOutletContext", i0.ɵɵpureFunction1(2, _c7, ctx_r0.defineId));
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Conditional_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "dot-area");
    i0.ɵɵtext(1, " Not Found. ");
    i0.ɵɵelementEnd();
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Conditional_1_Conditional_0_Template, 1, 4, "ng-container")(1, DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Conditional_1_Conditional_1_Template, 2, 0, "dot-area");
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(5);
    i0.ɵɵconditional(ctx_r0.emptyRef ? 0 : 1);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Conditional_0_Template, 1, 4, "dot-component", 5)(1, DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Conditional_1_Template, 2, 1);
  }
  if (rf & 2) {
    let tmp_5_0;
    const ctx_r0 = i0.ɵɵnextContext(4);
    i0.ɵɵconditional((tmp_5_0 = ctx_r0.configure.emptyComponent) ? 0 : 1, tmp_5_0);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_0_Template, 1, 5, "ng-container")(1, DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Conditional_1_Template, 2, 1);
  }
  if (rf & 2) {
    const response_r3 = i0.ɵɵnextContext();
    i0.ɵɵconditional(response_r3.length ? 0 : 1);
  }
}
function DotRenderComponent_Conditional_2_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Conditional_0_Template, 2, 6);
    i0.ɵɵpipe(1, "pathWalk");
    i0.ɵɵpipe(2, "typeOf");
    i0.ɵɵtemplate(3, DotRenderComponent_Conditional_2_Conditional_0_Conditional_3_Template, 2, 1);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵconditional(i0.ɵɵpipeBind1(2, 4, i0.ɵɵpipeBind2(1, 1, ctx, ctx_r0.paginateTotalProperty)) == "number" ? 0 : 3);
  }
}
function DotRenderComponent_Conditional_2_Conditional_2_Conditional_0_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function DotRenderComponent_Conditional_2_Conditional_2_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_2_Conditional_0_ng_container_0_Template, 1, 0, "ng-container", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.loaderRef);
  }
}
function DotRenderComponent_Conditional_2_Conditional_2_Conditional_1_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "dot-component", 5);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(4);
    i0.ɵɵproperty("option", ctx)("data", i0.ɵɵpureFunction1(2, _c6, ctx_r0.defineId));
  }
}
function DotRenderComponent_Conditional_2_Conditional_2_Conditional_1_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "dot-loader", 6);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(4);
    i0.ɵɵproperty("shape", ctx_r0.loaderShape || ctx_r0.configure.loaderShape);
  }
}
function DotRenderComponent_Conditional_2_Conditional_2_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_2_Conditional_1_Conditional_0_Template, 1, 4, "dot-component", 5)(1, DotRenderComponent_Conditional_2_Conditional_2_Conditional_1_Conditional_1_Template, 1, 1, "dot-loader", 6);
  }
  if (rf & 2) {
    let tmp_3_0;
    const ctx_r0 = i0.ɵɵnextContext(3);
    i0.ɵɵconditional((tmp_3_0 = ctx_r0.configure.loaderComponent) ? 0 : 1, tmp_3_0);
  }
}
function DotRenderComponent_Conditional_2_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_2_Conditional_0_Template, 1, 1, "ng-container")(1, DotRenderComponent_Conditional_2_Conditional_2_Conditional_1_Template, 2, 1);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext(2);
    i0.ɵɵconditional(ctx_r0.errorRef ? 0 : 1);
  }
}
function DotRenderComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, DotRenderComponent_Conditional_2_Conditional_0_Template, 4, 6);
    i0.ɵɵpipe(1, "async");
    i0.ɵɵtemplate(2, DotRenderComponent_Conditional_2_Conditional_2_Template, 2, 1);
  }
  if (rf & 2) {
    let tmp_1_0;
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵconditional((tmp_1_0 = i0.ɵɵpipeBind1(1, 1, ctx_r0.fetcher.fetch$)) ? 0 : 2, tmp_1_0);
  }
}
class DotRenderComponent {
  _module;
  _translate;
  defineId;
  fetcher;
  pageLinkType;
  pageLinkLimit;
  pageLinkExtend;
  loaderShape;
  paginateTotalProperty;
  paginateFilterProperty;
  paginateItemsProperty;
  contentRef;
  loaderRef;
  emptyRef;
  errorRef;
  load$;
  _hostClassList;
  get configure() {
    return this._module.configure;
  }
  constructor(_module, _translate, elementRef) {
    this._module = _module;
    this._translate = _translate;
    this._hostClassList = elementRef.nativeElement.classList;
    const {
      configure
    } = _module;
    this.paginateTotalProperty = configure.totalProperty;
    this.paginateItemsProperty = configure.itemsProperty;
    this.paginateFilterProperty = configure.filterProperty;
  }
  ngOnInit() {
    if (!this.contentRef) {
      throw new SyntaxError('`<ng-template #content></ng-template>` is Required.');
    }
    this.fetcher.fetchLoad$.subscribe(flag => {
      if (flag) {
        this._hostClassList.add('_loading');
      } else {
        this._hostClassList.remove('_loading');
      }
    });
  }
  setPage(page) {
    // todo: check
    // (this.fetcher as any)._caster.set('$page', page);
  }
  static ɵfac = function DotRenderComponent_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRenderComponent)(i0.ɵɵdirectiveInject(DotRenderModuleService), i0.ɵɵdirectiveInject(i1.DotTranslateService), i0.ɵɵdirectiveInject(i0.ElementRef));
  };
  static ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
    type: DotRenderComponent,
    selectors: [["dot-render"]],
    contentQueries: function DotRenderComponent_ContentQueries(rf, ctx, dirIndex) {
      if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, _c0, 7);
        i0.ɵɵcontentQuery(dirIndex, _c1, 5);
        i0.ɵɵcontentQuery(dirIndex, _c2, 5);
        i0.ɵɵcontentQuery(dirIndex, _c3, 5);
      }
      if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.contentRef = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.loaderRef = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.emptyRef = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.errorRef = _t.first);
      }
    },
    hostAttrs: ["style.display", "block", 1, "dot-render"],
    inputs: {
      defineId: "defineId",
      fetcher: "fetcher",
      pageLinkType: "pageLinkType",
      pageLinkLimit: "pageLinkLimit",
      pageLinkExtend: "pageLinkExtend",
      loaderShape: "loaderShape",
      paginateTotalProperty: "paginateTotalProperty",
      paginateFilterProperty: "paginateFilterProperty",
      paginateItemsProperty: "paginateItemsProperty"
    },
    decls: 3,
    vars: 3,
    consts: [[4, "ngTemplateOutlet"], [3, "option"], [4, "ngTemplateOutlet", "ngTemplateOutletContext"], [3, "meta", "type", "linkLimit", "extend"], [3, "changed$", "meta", "type", "linkLimit", "extend"], [3, "option", "data"], [3, "shape"]],
    template: function DotRenderComponent_Template(rf, ctx) {
      if (rf & 1) {
        i0.ɵɵtemplate(0, DotRenderComponent_Conditional_0_Template, 2, 1);
        i0.ɵɵpipe(1, "async");
        i0.ɵɵtemplate(2, DotRenderComponent_Conditional_2_Template, 3, 3);
      }
      if (rf & 2) {
        i0.ɵɵconditional(i0.ɵɵpipeBind1(1, 1, ctx.fetcher.fetchError$) ? 0 : 2);
      }
    },
    dependencies: [CommonModule, i2.NgTemplateOutlet, i2.AsyncPipe, DotLoaderModule, i3.DotLoaderComponent, DotPageLinksModule, i4.DotPageLinksComponent, DotPathWalkPipe, DotFrameModule, i5.DotAreaComponent, DotComponent, DotTypeOfPipe],
    styles: [".dot-render._loading{opacity:.64;position:relative}.dot-render._loading:before{inset:0;width:100%;height:100%;position:absolute}.dot-render .rx-external-searches{display:grid;gap:var(--pad);grid-template-columns:1fr}.dot-render .rx-external-searches .mat-mdc-form-field-infix{display:flex;flex-direction:row}.dot-render .rx-external-searches .mat-mdc-select{flex-grow:1}.dot-render .rx-external-searches .dot-link{line-height:1em}@media (480px <= width < 760px){.dot-render .rx-external-searches{grid-template-columns:repeat(2,1fr)}}@media (760px <= width < 1000px){.dot-render .rx-external-searches{grid-template-columns:repeat(3,1fr)}}@media (1000px <= width){.dot-render .rx-external-searches{grid-template-columns:repeat(4,1fr)}}\n"],
    encapsulation: 2,
    changeDetection: 0
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRenderComponent, [{
    type: Component,
    args: [{
      imports: [CommonModule, DotLoaderModule, DotPageLinksModule, DotPathWalkPipe, DotFrameModule, DotComponent, DotTypeOfPipe
      // DotHasOwnPipe,
      // DotPaginatorComponent
      ],
      selector: 'dot-render',
      template: `
		@if (fetcher.fetchError$ | async) {
			@if (errorRef) {
				<ng-container *ngTemplateOutlet="errorRef"/>
			} @else {
				@if (configure.errorComponent; as option) {
					<dot-component [option]="option"/>
				} @else {
					<dot-area>
						Rendering has something trouble.
					</dot-area>
				}
			}
		} @else {
			@if (fetcher.fetch$ | async; as response) {
				@if ((response | pathWalk: paginateTotalProperty | typeOf) == 'number') {
					@if (response | pathWalk: paginateItemsProperty: []; as items) {
						@if ((response | pathWalk:paginateFilterProperty) || items.length) {
							<ng-container
								*ngTemplateOutlet="contentRef; context: {$implicit: response, items: items}"/>
							@if (response.meta.total > 0) {
								<dot-page-links
									[meta]="response.meta"
									[type]="pageLinkType"
									[linkLimit]="pageLinkLimit"
									[extend]="pageLinkExtend"
									(changed$)="setPage($event)"
								/>
<!--								<dot-paginator-->
<!--									[meta]="response.meta"-->
<!--								/>-->
							}
						} @else {
							@if (configure.emptyComponent; as option) {
								<dot-component [option]="option" [data]="{defineId: defineId}"/>
							} @else {
								@if (emptyRef) {
									<ng-container *ngTemplateOutlet="emptyRef; context: {$implicit: defineId}"/>
								} @else {
									<dot-area>
										Not Found.
									</dot-area>
								}
							}
						}
					}
				} @else {
					@if (response.length) {
						<ng-container *ngTemplateOutlet="contentRef; context: {$implicit: response, items: response}"/>
					} @else {
						@if (configure.emptyComponent; as option) {
							<dot-component [option]="option" [data]="{defineId: defineId}"/>
						} @else {
							@if (emptyRef) {
								<ng-container *ngTemplateOutlet="emptyRef; context: {$implicit: defineId}"/>
							} @else {
								<dot-area>
									Not Found.
								</dot-area>
							}
						}
					}
				}
			} @else {
				@if (errorRef) {
					<ng-container *ngTemplateOutlet="loaderRef"></ng-container>
				} @else {
					@if (configure.loaderComponent; as option) {
						<dot-component [option]="option" [data]="{defineId: defineId}"></dot-component>
					} @else {
						<dot-loader [shape]="loaderShape || configure.loaderShape"></dot-loader>
					}
				}
			}
		}




	`,
      encapsulation: ViewEncapsulation.None,
      changeDetection: ChangeDetectionStrategy.OnPush,
      host: {
        class: 'dot-render',
        'style.display': 'block'
      },
      styles: [".dot-render._loading{opacity:.64;position:relative}.dot-render._loading:before{inset:0;width:100%;height:100%;position:absolute}.dot-render .rx-external-searches{display:grid;gap:var(--pad);grid-template-columns:1fr}.dot-render .rx-external-searches .mat-mdc-form-field-infix{display:flex;flex-direction:row}.dot-render .rx-external-searches .mat-mdc-select{flex-grow:1}.dot-render .rx-external-searches .dot-link{line-height:1em}@media (480px <= width < 760px){.dot-render .rx-external-searches{grid-template-columns:repeat(2,1fr)}}@media (760px <= width < 1000px){.dot-render .rx-external-searches{grid-template-columns:repeat(3,1fr)}}@media (1000px <= width){.dot-render .rx-external-searches{grid-template-columns:repeat(4,1fr)}}\n"]
    }]
  }], () => [{
    type: DotRenderModuleService
  }, {
    type: i1.DotTranslateService
  }, {
    type: i0.ElementRef
  }], {
    defineId: [{
      type: Input
    }],
    fetcher: [{
      type: Input
    }],
    pageLinkType: [{
      type: Input
    }],
    pageLinkLimit: [{
      type: Input
    }],
    pageLinkExtend: [{
      type: Input
    }],
    loaderShape: [{
      type: Input
    }],
    paginateTotalProperty: [{
      type: Input
    }],
    paginateFilterProperty: [{
      type: Input
    }],
    paginateItemsProperty: [{
      type: Input
    }],
    contentRef: [{
      type: ContentChild,
      args: ['content', {
        static: true
      }]
    }],
    loaderRef: [{
      type: ContentChild,
      args: ['loader']
    }],
    emptyRef: [{
      type: ContentChild,
      args: ['empty']
    }],
    errorRef: [{
      type: ContentChild,
      args: ['error']
    }]
  });
})();
const DOT_RENDER_MODULE_CONFIGURE = new InjectionToken('#ng-dot/ui/render/configure');
class DotRenderModuleService {
  configure;
  constructor(configure) {
    this.configure = {
      totalProperty: 'meta.total',
      filterProperty: 'meta.filtered',
      itemsProperty: 'items',
      loaderShape: 'circle',
      ...(configure || {})
    };
  }
  static ɵfac = function DotRenderModuleService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRenderModuleService)(i0.ɵɵinject(DOT_RENDER_MODULE_CONFIGURE, 8));
  };
  static ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: DotRenderModuleService,
    factory: DotRenderModuleService.ɵfac,
    providedIn: 'root'
  });
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRenderModuleService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: undefined,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [DOT_RENDER_MODULE_CONFIGURE]
    }]
  }], null);
})();
class DotRenderModule {
  static forRoot(configure) {
    return {
      ngModule: DotRenderModule,
      providers: [{
        provide: DOT_RENDER_MODULE_CONFIGURE,
        useValue: configure
      }]
    };
  }
  static ɵfac = function DotRenderModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || DotRenderModule)();
  };
  static ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: DotRenderModule
  });
  static ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(DotRenderModule, [{
    type: NgModule
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { DOT_RENDER_MODULE_CONFIGURE, DotRenderComponent, DotRenderModule, DotRenderModuleService };
